import React from 'react';
import { PALETTE } from './SiteLayout';
import CashelBlueExportMap from '../../components/CashelBlueExportMap';

/**
 * Cashel Blue founding customer showcase.
 *
 * NOTE on testimonials: we deliberately lead with verifiable facts about the
 * working relationship (active IT contract, features built in response to
 * floor-team feedback, length of partnership) rather than thin one-line
 * quotes. Stronger long-form testimonials will be swapped in once collected
 * directly from Louis Clifton Brown (QA) and Sarah Furno (Co-owner).
 */

const RELATIONSHIP_FACTS = [
  { label: 'IT contract since', value: '2024' },
  { label: 'Live on Keystone since', value: 'February 2026' },
  { label: 'Project lead', value: 'Asif Khan, with Louis Clifton Brown (QA & Operations Manager)' },
  { label: 'Replacing', value: 'A bespoke Excel + macros order plan, manual dispatch notes, batch tracking on paper' },
  { label: 'Status', value: 'Live in production · multi-award-winning Tipperary farmhouse dairy · international distribution' },
];

const FEATURES_FROM_CASHEL = [
  'Variable-weight batch capture',
  'Retailer-specific code mapping with reverse lookup',
  'Split-batch dispatch with auto-reconciliation',
  'Use-by date tracking by batch',
  'AccountsIQ two-way sync',
  'Global dispatch search by document number',
];

const OUTCOMES = [
  { metric: '4 sec', label: 'Dispatch note generation', sub: 'Down from 8–12 minutes in Excel' },
  { metric: '100%', label: 'Batch trace coverage', sub: 'Forward + backward, BRCGS traceability ready' },
  { metric: '0', label: 'Spreadsheets in dispatch', sub: 'Replaced from day-one go-live' },
];

export function CashelBlueLogoStrip() {
  return (
    <section style={{
      background: PALETTE.cream,
      padding: '48px 32px',
      borderTop: `1px solid ${PALETTE.border}`,
      borderBottom: `1px solid ${PALETTE.border}`,
    }} data-testid="cashel-blue-logo-strip">
      <div style={{
        maxWidth: 1080,
        margin: '0 auto',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 40,
        flexWrap: 'wrap',
      }}>
        <div style={{
          fontSize: 11,
          letterSpacing: '0.22em',
          textTransform: 'uppercase',
          fontWeight: 700,
          color: PALETTE.muted,
          textAlign: 'center',
        }}>
          Built with, and used by, Cashel Farmhouse Cheesemakers, Tipperary
        </div>
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: 18,
          padding: '12px 22px',
          background: '#fff',
          border: `1px solid ${PALETTE.border}`,
          borderRadius: 8,
          boxShadow: '0 4px 14px rgba(10,37,64,0.05)',
        }}>
          <img
            src="/customers/cashel-blue-logo.webp"
            alt="Cashel Farmhouse Cheesemakers"
            style={{ height: 64, width: 'auto', display: 'block', objectFit: 'contain' }}
          />
          <div style={{ borderLeft: `1px solid ${PALETTE.border}`, height: 48 }} />
          <div>
            <div style={{
              fontSize: 9.5,
              letterSpacing: '0.18em',
              textTransform: 'uppercase',
              fontWeight: 700,
              color: PALETTE.magenta,
            }}>Founding customer</div>
            <div style={{
              fontFamily: "'Playfair Display', Georgia, serif",
              fontSize: 16,
              fontWeight: 700,
              color: PALETTE.navy,
              letterSpacing: '-0.01em',
              marginTop: 2,
            }}>Cashel Farmhouse Cheesemakers</div>
            <div style={{ fontSize: 11, color: PALETTE.muted, marginTop: 1, fontStyle: 'italic' }}>
              Fethard, Co. Tipperary
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

export function CashelBlueCaseStudy() {
  return (
    <section style={{
      background: PALETTE.paper,
      padding: '120px 32px',
      borderTop: `1px solid ${PALETTE.border}`,
    }} data-testid="cashel-blue-case-study">
      <div style={{ maxWidth: 1280, margin: '0 auto' }}>
        {/* Heading */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: '1fr 1.4fr',
          gap: 56,
          alignItems: 'end',
          marginBottom: 56,
        }} className="cb-head">
          <div>
            <div style={{
              fontSize: 11, letterSpacing: '0.22em', textTransform: 'uppercase',
              fontWeight: 700, color: PALETTE.magenta, marginBottom: 16,
            }}>Founding customer · case study</div>
            <h2 style={{
              fontFamily: "'Playfair Display', Georgia, serif",
              fontSize: 'clamp(32px, 4.4vw, 50px)',
              fontWeight: 700, color: PALETTE.navy,
              margin: 0, letterSpacing: '-0.02em', lineHeight: 1.05,
            }}>
              Built with<br/>
              <em style={{ fontStyle: 'italic', fontWeight: 500, color: PALETTE.magenta }}>Cashel Blue<sup style={{ fontSize: '0.5em', verticalAlign: 'super' }}>®</sup>.</em>
            </h2>
          </div>
          <p style={{ fontSize: 18, lineHeight: 1.65, color: PALETTE.slate, margin: 0 }}>
            Keystone wasn't designed in a boardroom. It was built side-by-side with <strong style={{ color: PALETTE.navy, fontWeight: 600 }}>Cashel Farmhouse Cheesemakers</strong> in Fethard, Co. Tipperary, a multi-award-winning Irish farmhouse dairy with international distribution, family-run by Sarah and Sergio Furno. <span style={{ fontSize: 13, color: PALETTE.muted, fontStyle: 'italic' }}>Cashel Blue® and Crozier Blue® are registered trademarks of J&L Grubb Ltd; brand name, team photography and operational figures used with the written permission of J&L Grubb Ltd.</span>
            <br/><br/>
            It started with a complex Excel-and-macro order plan their QA Manager Louis was building. We rebuilt it from scratch as a purpose-fit operational system around how a real artisan dairy actually runs, variable-weight batches, retailer code mapping, batch traceability, split-batch dispatch, AccountsIQ sync, use-by tracking. Things accounting packages can't do.
          </p>
        </div>

        {/* 3-column showcase */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: '0.9fr 1.1fr 1fr',
          gap: 0,
          border: `1px solid ${PALETTE.border}`,
          borderRadius: 10,
          overflow: 'hidden',
          background: '#fff',
        }} className="cb-grid">
          {/* Left: brand + photo */}
          <div style={{
            position: 'relative',
            background: PALETTE.bgSoft,
            minHeight: 420,
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'space-between',
            padding: 32,
            borderRight: `1px solid ${PALETTE.border}`,
          }} className="cb-brand">
            <div>
              <img
                src="/customers/cashel-blue-logo.webp"
                alt="Cashel Farmhouse Cheesemakers"
                style={{ height: 110, width: 'auto', display: 'block', objectFit: 'contain' }}
              />
              <div style={{
                marginTop: 18,
                fontSize: 10.5, letterSpacing: '0.22em', textTransform: 'uppercase',
                fontWeight: 700, color: PALETTE.magenta,
              }}>Founding customer</div>
              <div style={{
                marginTop: 6,
                fontFamily: "'Playfair Display', Georgia, serif",
                fontSize: 22, fontWeight: 700, color: PALETTE.navy,
                letterSpacing: '-0.01em', lineHeight: 1.2,
              }}>
                Cashel Farmhouse<br/>Cheesemakers
              </div>
              <div style={{ fontSize: 13, color: PALETTE.muted, marginTop: 8, lineHeight: 1.5, fontStyle: 'italic' }}>
                Beechmount Farm · Fethard<br/>Co. Tipperary, Ireland
              </div>
            </div>
            <div style={{
              marginTop: 24,
              borderRadius: 6, overflow: 'hidden',
              border: `1px solid ${PALETTE.border}`,
              boxShadow: '0 8px 20px rgba(10,37,64,0.08)',
            }}>
              <img
                src="/customers/cashel-blue-team.png"
                alt="The Cashel Farmhouse Cheesemakers team, Beechmount Farm, Co. Tipperary"
                style={{ display: 'block', width: '100%', height: 200, objectFit: 'cover', objectPosition: 'center 35%' }}
              />
            </div>
          </div>

          {/* Middle: working relationship, facts, not thin quotes */}
          <div style={{ padding: 36, borderRight: `1px solid ${PALETTE.border}`, display: 'flex', flexDirection: 'column', gap: 22 }}>
            <div style={{
              fontSize: 10.5, letterSpacing: '0.18em', textTransform: 'uppercase',
              fontWeight: 700, color: PALETTE.muted,
            }}>The working relationship</div>

            <div>
              {RELATIONSHIP_FACTS.map((f) => (
                <div key={f.label} style={{
                  padding: '12px 0',
                  borderBottom: `1px solid ${PALETTE.border}`,
                  display: 'grid',
                  gridTemplateColumns: '110px 1fr',
                  gap: 16,
                  alignItems: 'baseline',
                }}>
                  <div style={{
                    fontSize: 10, letterSpacing: '0.14em', textTransform: 'uppercase',
                    fontWeight: 700, color: PALETTE.muted,
                  }}>{f.label}</div>
                  <div style={{
                    fontSize: 14.5, lineHeight: 1.5, color: PALETTE.ink, fontWeight: 500,
                  }}>{f.value}</div>
                </div>
              ))}
            </div>

            <div style={{
              padding: '14px 16px',
              background: PALETTE.bgSoft,
              borderLeft: `3px solid ${PALETTE.magenta}`,
              borderRadius: 4,
              fontSize: 13.5,
              lineHeight: 1.6,
              color: PALETTE.ink,
            }}>
              <div style={{
                fontSize: 10, letterSpacing: '0.18em', textTransform: 'uppercase',
                fontWeight: 700, color: PALETTE.magenta, marginBottom: 6,
              }}>Features shipped from Cashel Blue's floor</div>
              {FEATURES_FROM_CASHEL.join(' · ')}
            </div>

            <div style={{ fontSize: 12, color: PALETTE.muted, fontStyle: 'italic', lineHeight: 1.55 }}>
              Long-form testimonials from Louis Clifton Brown and Sarah Furno are being collected for publication later this month.
            </div>
          </div>

          {/* Right: outcome metrics */}
          <div style={{
            padding: 36,
            background: PALETTE.navy,
            color: '#fff',
            display: 'flex',
            flexDirection: 'column',
            gap: 20,
          }}>
            <div style={{
              fontSize: 10.5, letterSpacing: '0.18em', textTransform: 'uppercase',
              fontWeight: 700, color: PALETTE.gold,
            }}>What changed on the floor</div>

            {OUTCOMES.map((o, i) => (
              <div key={o.label} style={{
                paddingTop: i === 0 ? 0 : 16,
                borderTop: i === 0 ? 'none' : '1px solid rgba(255,255,255,0.10)',
              }}>
                <div style={{
                  fontFamily: "'Playfair Display', Georgia, serif",
                  fontSize: 38, fontWeight: 700, color: PALETTE.gold,
                  letterSpacing: '-0.02em', lineHeight: 1,
                }}>{o.metric}</div>
                <div style={{ fontSize: 14, fontWeight: 700, color: '#fff', marginTop: 6 }}>
                  {o.label}
                </div>
                <div style={{ fontSize: 12, color: '#94A3B8', marginTop: 3, lineHeight: 1.5 }}>
                  {o.sub}
                </div>
              </div>
            ))}

            <div style={{
              marginTop: 'auto',
              padding: '12px 14px',
              background: 'rgba(201, 169, 97, 0.10)',
              border: `1px solid ${PALETTE.gold}40`,
              borderRadius: 4,
              fontSize: 11.5,
              lineHeight: 1.5,
              color: '#E5E7EB',
            }}>
              <strong style={{ color: PALETTE.gold, fontWeight: 700 }}>Built for:</strong> variable-weight batches, retailer EDI codes, batch traceability, split-batch dispatch, AccountsIQ sync.
            </div>
          </div>
        </div>

        {/* International scale, stylised export map */}
        <div style={{ marginTop: 48 }}>
          <CashelBlueExportMap variant="dark" />
        </div>
      </div>
      <style>{`
        @media (max-width: 1100px) {
          .cb-head { grid-template-columns: 1fr !important; gap: 32px !important; align-items: start !important; }
          .cb-grid { grid-template-columns: 1fr !important; }
          .cb-grid > div { border-right: none !important; border-bottom: 1px solid ${PALETTE.border} !important; }
          .cb-grid > div:last-child { border-bottom: none !important; }
          .cb-brand { min-height: auto !important; }
        }
      `}</style>
    </section>
  );
}

export function CashelBlueCompactQuote() {
  return (
    <div style={{
      padding: 28,
      background: '#fff',
      border: `1px solid ${PALETTE.border}`,
      borderLeft: `3px solid ${PALETTE.magenta}`,
      borderRadius: 6,
    }} data-testid="cashel-blue-compact-quote">
      <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 18 }}>
        <img
          src="/customers/cashel-blue-logo.webp"
          alt="Cashel Blue"
          style={{ height: 64, width: 'auto', objectFit: 'contain' }}
        />
        <div style={{ borderLeft: `1px solid ${PALETTE.border}`, paddingLeft: 16 }}>
          <div style={{
            fontSize: 9.5, letterSpacing: '0.18em', textTransform: 'uppercase',
            fontWeight: 700, color: PALETTE.magenta,
          }}>Founding customer</div>
          <div style={{
            fontFamily: "'Playfair Display', Georgia, serif",
            fontSize: 14, fontWeight: 700, color: PALETTE.navy, letterSpacing: '-0.01em',
            marginTop: 2,
          }}>Cashel Farmhouse Cheesemakers</div>
        </div>
      </div>
      <div style={{
        fontFamily: "'Playfair Display', Georgia, serif",
        fontSize: 18, lineHeight: 1.4,
        color: PALETTE.navy, fontWeight: 600, letterSpacing: '-0.01em',
      }}>
        Live on Keystone since February 2026.<br/>
        <em style={{ fontStyle: 'italic', color: PALETTE.magenta, fontWeight: 500 }}>Built around their floor, not the other way around.</em>
      </div>
      <div style={{
        marginTop: 16,
        paddingTop: 14,
        borderTop: `1px solid ${PALETTE.border}`,
        fontSize: 12.5, lineHeight: 1.55, color: PALETTE.slate,
      }}>
        Active IT contract with J&L Grubb Ltd, trading as Cashel Farmhouse Cheesemakers, Tipperary. Six core Keystone features were shipped in direct response to feedback from QA & Operations Manager Louis Clifton Brown. Cashel Blue<sup style={{ fontSize: '0.7em' }}>®</sup> and Crozier Blue<sup style={{ fontSize: '0.7em' }}>®</sup> are registered trademarks of J&L Grubb Ltd; brand references used with written permission. A direct reference call can be arranged following an initial discovery call, please <a href="/site/contact" style={{ color: PALETTE.magenta, fontWeight: 600 }}>request via Contact</a>. See <a href="/legal/permissions" style={{ color: PALETTE.magenta, fontWeight: 600 }}>permissions log</a> for the full attribution.
      </div>
    </div>
  );
}
